/**
 * Utility functions for filtering rental-related collections from credit reports.
 *
 * This module provides multi-provider support for identifying rental collections
 * from both TransUnion (short bureau codes) and Experian (descriptive strings).
 */

import {
  RENTAL_COLLECTION_PATTERNS,
  RENTAL_INDUSTRY_CODES,
  EXPERIAN_RENTAL_ACCOUNT_TYPES_NORMALIZED,
  EXPERIAN_RENTAL_KOB_DESCRIPTIONS_NORMALIZED,
} from "shared/config/constants/rentalCollections.constants";
import { CollectionAccount } from "shared/interfaces/creditAndBackgroundCheck";

const REGEX_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

/**
 * Escapes special regex characters in a string.
 */
const escapeForRegex = (value: string): string =>
  value.replace(REGEX_SPECIAL_CHARS, "\\$&");

/**
 * Normalizes text for comparison (trim + lowercase).
 */
const normalizeText = (value: string): string => value.trim().toLowerCase();

/**
 * Checks if normalized text contains a normalized phrase as a whole word/phrase.
 * Uses word boundaries to prevent partial matches (e.g., "Release" won't match "Lease").
 */
const containsNormalizedPhrase = (
  normalizedText: string,
  normalizedPhrase: string
): boolean => {
  if (!normalizedText || !normalizedPhrase) {
    return false;
  }

  const pattern = new RegExp(
    `(^|\\b)${escapeForRegex(normalizedPhrase)}(\\b|$)`
  );

  return pattern.test(normalizedText);
};

/**
 * Checks if collection has Experian rental signals.
 * Uses partial string matching for both Account Types & KOB descriptions.
 *
 * Experian uses descriptive strings like "Lease", "Mortgage Reporters",
 * instead of short bureau codes.
 *
 * @param collection - Collection account to check
 * @returns Object with rental signal flags
 */
const getExperianRentalSignals = (collection: CollectionAccount) => {
  const loanType = (collection?.loanType ?? "").trim();
  const industryCode = (collection?.industryCode ?? "").trim();
  const normalizedLoanType = normalizeText(loanType);
  const normalizedIndustryCode = normalizeText(industryCode);

  // Check if loanType contains any rental account type description
  const hasRentalLoanType =
    !!normalizedLoanType &&
    EXPERIAN_RENTAL_ACCOUNT_TYPES_NORMALIZED.some((type) =>
      containsNormalizedPhrase(normalizedLoanType, type)
    );

  // Check if industryCode (KOB) contains any rental industry description
  const hasRentalIndustryCode =
    !!normalizedIndustryCode &&
    EXPERIAN_RENTAL_KOB_DESCRIPTIONS_NORMALIZED.some((kob) =>
      containsNormalizedPhrase(normalizedIndustryCode, kob)
    );

  return {
    hasRentalLoanType,
    hasRentalIndustryCode,
  };
};

/**
 * Checks if collection has TransUnion rental signals.
 * Uses exact matching for bureau codes.
 *
 * TransUnion uses short code abbreviations like "LE", "RA", "R" for
 * loan types and industry classifications.
 *
 * @param collection - Collection account to check
 * @returns Object with rental signal flags
 */
const getTransUnionRentalSignals = (collection: CollectionAccount) => {
  const loanType = (collection?.loanType ?? "").trim();
  const industryCode = collection?.industryCode?.toUpperCase() ?? "";

  // Check exact matches for TransUnion codes or full string matches
  const hasRentalLoanType = RENTAL_COLLECTION_PATTERNS.loanTypes.test(loanType);

  // Check exact match for TransUnion industry codes
  const hasRentalIndustryCode = RENTAL_INDUSTRY_CODES.some(
    (code) => code === industryCode
  );

  return {
    hasRentalLoanType,
    hasRentalIndustryCode,
  };
};

/**
 * Gets combined rental signals from both TransUnion and Experian detection logic.
 * Uses OR logic to combine signals - if either provider's logic matches, consider it valid.
 * Avoids the need to detect which bureau provided the data.
 *
 * @param collection - Collection account to check
 * @returns Combined rental signal flags
 */
const getCombinedRentalSignals = (collection: CollectionAccount) => {
  const tuSignals = getTransUnionRentalSignals(collection);
  const experianSignals = getExperianRentalSignals(collection);

  return {
    hasRentalLoanType:
      tuSignals.hasRentalLoanType || experianSignals.hasRentalLoanType,
    hasRentalIndustryCode:
      tuSignals.hasRentalIndustryCode || experianSignals.hasRentalIndustryCode,
  };
};

/**
 * Filters a list of collection accounts to only include rental-related collections.
 *
 * Multi-provider implementation that works with both TransUnion and Experian data:
 * - **TransUnion**: Uses exact code matching (e.g., "LE", "RA", "R")
 * - **Experian**: Uses partial string matching for descriptive fields (e.g., "Lease", "Mortgage Reporters")
 * - **Provider-agnostic**: Tries both approaches simultaneously without needing to detect the source
 *
 * This function distinguishes rental collections (apartments, housing, etc.) from
 * non-rental collections (medical bills, student loans, etc.) using keyword matching
 * on creditor names, comments, and loan type codes.
 *
 * Filtering logic:
 * 1. Exclude collections with medical/student loan keywords in comments or creditor name
 * 2. Check for rental signals using BOTH TransUnion and Experian matching logic (OR logic)
 * 3. Require corroborating rental signals (loan type, industry code, or rental keywords)
 *
 * This defensive approach ensures collections from either bureau are correctly identified
 * without requiring fragile provider detection heuristics.
 *
 * @param collections - Array of collection accounts from credit report
 * @returns Filtered array containing only rental-related collections
 *
 * @example
 * ```typescript
 * const allCollections = creditReport.collections
 * const rentalOnly = filterRentalCollections(allCollections)
 * // rentalOnly now contains only apartment, housing, etc. collections
 * ```
 */
export const filterRentalCollections = (
  collections: CollectionAccount[]
): CollectionAccount[] => {
  if (!collections?.length) {
    return [];
  }

  return collections.filter((collection) => {
    const comments = collection?.comments ?? "";
    const creditorName = collection?.creditorsName ?? "";

    // Exclude medical and student loan collections
    if (
      RENTAL_COLLECTION_PATTERNS.exclude.test(comments) ||
      RENTAL_COLLECTION_PATTERNS.exclude.test(creditorName)
    ) {
      return false;
    }

    // Get combined rental signals from both TransUnion and Experian logic
    // Uses OR logic - if either provider's matching finds a signal, accept it
    const { hasRentalLoanType, hasRentalIndustryCode } =
      getCombinedRentalSignals(collection);

    // Check for rental keywords in text fields
    const hasRentalKeywordInName =
      RENTAL_COLLECTION_PATTERNS.include.test(creditorName);
    const hasRentalKeywordInComments =
      RENTAL_COLLECTION_PATTERNS.include.test(comments);
    const hasRentalTextSignal =
      hasRentalKeywordInName || hasRentalKeywordInComments;

    // Allow explicit rental loan types even without additional signals
    if (hasRentalLoanType) {
      return true;
    }

    // Require rental text indicators when relying on industry codes
    if (hasRentalIndustryCode && hasRentalTextSignal) {
      return true;
    }

    // Otherwise insufficient evidence of rental collection
    return false;
  });
};
